import React, { useEffect, useState } from 'react';
import { useLanguage } from './LanguageContext';
import { MenuIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { artwork } from './artwork';
import { Link, useLocation, useNavigate } from 'react-router-dom';
export function Navbar() {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    // Extract the hash (e.g. "about" from "/#about")
    const id = href.replace('/#', '').replace('#', '');
    if (isHomePage) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      navigate('/');
      // Wait for the homepage to render, then scroll
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };
  const navLinks = [{
    name: t.nav.home,
    href: '/#home',
    isRoute: false
  }, {
    name: t.nav.testimonials,
    href: '/testimonials',
    isRoute: true
  }, {
    name: t.nav.recommendations,
    href: '/recommendations',
    isRoute: true
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-171">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-172">
        <div className="flex justify-between items-center" data-id="element-173">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-id="element-174">
            <img src={artwork.LOGO_IMAGE} alt="Aissa Edon Logo" className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover border border-teal-100 shadow-sm group-hover:shadow-md transition-shadow" data-id="element-175" />
            <div className="flex flex-col" data-id="element-176">
              <span className="text-xl md:text-2xl font-serif font-semibold text-teal-800 leading-tight" data-id="element-177">
                Aissa Edon
              </span>
            </div>
          </Link>

          {/* Right side: Language Toggle + Book Now + Hamburger */}
          <div className="flex items-center space-x-3 sm:space-x-5" data-id="element-178">
            {/* Language Switcher */}
            <div className="flex items-center bg-slate-100 rounded-full p-1" data-id="element-179">
              <button onClick={() => setLanguage('en')} className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${language === 'en' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-180">
                EN
              </button>
              <button onClick={() => setLanguage('fr')} className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${language === 'fr' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-181">
                FR
              </button>
            </div>

            {/* Book Now */}
            <Link to="/book-online" className="hidden sm:inline-flex bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors shadow-sm hover:shadow-md" data-id="element-182">
              {t.nav.bookNow}
            </Link>

            {/* Hamburger Menu Toggle */}
            <button className="p-2 text-slate-600 hover:text-teal-600 transition-colors rounded-lg hover:bg-teal-50" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle navigation menu" data-id="element-183">
              {isMenuOpen ? <XIcon size={24} data-id="element-184" /> : <MenuIcon size={24} data-id="element-185" />}
            </button>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence data-id="element-186">
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="bg-white border-t border-slate-100 shadow-lg overflow-hidden" data-id="element-187">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-188">
              <nav className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-id="element-189">
                {navLinks.map(link => link.isRoute ? <Link key={link.name} to={link.href} onClick={() => setIsMenuOpen(false)} className="text-base font-medium text-slate-700 hover:text-teal-600 hover:bg-teal-50 block py-3 px-4 rounded-xl transition-colors" data-id="element-190">
                      {link.name}
                    </Link> : <a key={link.name} href={link.href} onClick={e => handleNavClick(e, link.href)} className="text-base font-medium text-slate-700 hover:text-teal-600 hover:bg-teal-50 block py-3 px-4 rounded-xl transition-colors" data-id="element-191">
                      {link.name}
                    </a>)}
              </nav>

              {/* Mobile-only Book Now (visible on small screens where header button is hidden) */}
              <div className="sm:hidden mt-4 pt-4 border-t border-slate-100" data-id="element-192">
                <Link to="/book-online" onClick={() => setIsMenuOpen(false)} className="w-full text-center block bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-xl text-base font-medium transition-colors" data-id="element-193">
                  {t.nav.bookNow}
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}